import { RcIcon } from '@/components/atoms/RcIcon'
import { RcSuspense } from '@/components/atoms/RcSuspense'
import { RcTrans } from '@/components/atoms/RcTrans'
import { SearchBox } from '@/components/molecules/input/SearchBox'
import { StyledCheckbox } from '@/components/molecules/input/StyledCheckbox'
import {
  ActionMenu,
  ActionMenuOption
} from '@/components/molecules/interactive/ActionMenu'
import { ConfirmingButton } from '@/components/molecules/interactive/ConfirmingButton'
import { CreateButton } from '@/components/molecules/interactive/CreateButton'
import { RcIconButton } from '@/components/molecules/interactive/RcIconButton'
import { ModalTrigger } from '@/components/organisms/modal/ModalTrigger'
import { PageItem } from '@/components/organisms/page/PageItem'
import { usePage } from '@/components/providers/site/PageProvider'
import { pulse } from '@/core/animations'
import { ErrorCode } from '@/core/error-code'
import { useKeyPressed } from '@/core/hooks/useKeydown'
import { useIsTablet } from '@/core/hooks/useMediaQueries'
import { useNavigation } from '@/core/hooks/useNavigation'
import { RootRoute } from '@/core/route-keys'
import { useCompetitionTeamsIncludingIssues } from '@/entity/competition-team/useCompetitionTeams'
import { useCompetition } from '@/entity/competition/useCompetition'
import { useCompetitionEvents } from '@/entity/competition/useCompetitionEvents'
import { JoinFlow } from '@/flows/Competition/JoinFlowXstate/_JoinFlow'
import { useHotkeys } from '@mantine/hooks'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { EventKind } from '@rallycry/api-suite-typescript'
import { first, isEmpty, last, some, sortBy, uniq } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { usePopperMenu } from '../../../../../core/hooks/usePopperMenu'
import {
  RcTable,
  RcTableBody,
  RcTableCell,
  RcTableContainer,
  RcTableHead,
  RcTableHeaderRow,
  RcTableStickyHeaderCell
} from '../../../../molecules/text/RcTable'
import { BracketCreateEditForm } from '../bracket/BracketCreateEdit/BracketCreateEditForm'
import { CloneTeams } from '../shared/CloneTeams'
import { ParticipantContact } from '../shared/contact/ContactTeamsModal'
import { TeamAdminCreate } from '../team/TeamAdminCreate'
import { TeamReindex } from '../team/TeamReindex'
import { CheckInStatusMenu } from './CheckInStatusMenu'
import { FilterMode } from './FilterMode'
import { FormStatusMenu } from './FormStatusMenu'
import { RequiredNetworksMenu } from './RequiredNetworksMenu'
import { SortMode } from './SortMode'
import { TeamAdminJoyride } from './TeamAdminJoyride'
import { TeamMembersMenu } from './TeamMembersMenu'
import { TeamStatusMenu } from './TeamStatusMenu'
import { TeamsAdminTableRow } from './TeamsAdminTableRow'
import { TeamsSeeding } from './TeamsSeeding'

const HAS_OR_NOT_HAS_ISSUES = [FilterMode.HasIssues, FilterMode.NoIssues]
const init = new Date()

export const TeamsAdminDisplay = ({ selfOnly }: { selfOnly?: boolean }) => {
  const [run, setRun] = useState<Date>()
  const [lastValidated, setLastValidated] = useState<Date>(init)
  const [selectedTeams, setSelectedTeams] = useState<number[]>([])
  const [lastSelected, setLastSelected] = useState<number>()
  const [nameFilter, setNameFilter] = useState('')
  const [nameFilterDebounced] = useDebounce(nameFilter, 250)
  const [filterMode, setFilterMode] = useState<FilterMode>()
  const [sortMode, setSortMode] = useState<SortMode>()

  const isTablet = useIsTablet()
  const { getLinkUrl } = useNavigation()
  const { open } = usePage()
  const isShiftPressed = useKeyPressed('Shift')
  const { competition, settings, isCompetitionModerator } = useCompetition()
  const { events, flat } = useCompetitionEvents()
  const checkinEvents = events([EventKind.CHECK_IN])

  useHotkeys([['mod + r', () => handleRefresh()]])
  const menu = usePopperMenu()

  useEffect(() => {
    setSelectedTeams([])
  }, [filterMode])

  const {
    teams: source,
    updateTeams,
    checkInTeams,
    query: { isValidating, mutate: mutateTeams }
  } = useCompetitionTeamsIncludingIssues({
    selfOnly,
    suspense: false,
    request: {
      includeDropped: [...HAS_OR_NOT_HAS_ISSUES, FilterMode.Dropped].includes(
        filterMode!
      ),
      includeFormResponse: [
        ...HAS_OR_NOT_HAS_ISSUES,
        FilterMode.FormsCompleted,
        FilterMode.FormsMissing
      ].includes(filterMode!),
      includeCheckIn: [
        ...HAS_OR_NOT_HAS_ISSUES,
        FilterMode.CheckedIn,
        FilterMode.NotCheckedIn
      ].includes(filterMode!),
      includePermit: HAS_OR_NOT_HAS_ISSUES.includes(filterMode!),
      includeTeamSize: [
        ...HAS_OR_NOT_HAS_ISSUES,
        FilterMode.TeamComplete,
        FilterMode.TeamIncomplete
      ].includes(filterMode!),
      includeRequireNetwork: [
        ...HAS_OR_NOT_HAS_ISSUES,
        FilterMode.RequireNetwork,
        FilterMode.NotRequireNetwork
      ].includes(filterMode!),
      competitionId: competition?.id!
    }
  })

  const filtered = useMemo(
    () =>
      source.filter(it =>
        filterMode === FilterMode.HasIssues
          ? some(it._issues)
          : filterMode === FilterMode.NoIssues
            ? !some(it._issues)
            : filterMode === FilterMode.CheckedIn
              ? !some(
                  it._issues,
                  that => that.code === ErrorCode.CheckInRequired
                )
              : filterMode === FilterMode.NotCheckedIn
                ? some(
                    it._issues,
                    that => that.code === ErrorCode.CheckInRequired
                  )
                : filterMode === FilterMode.Dropped
                  ? some(
                      it._issues,
                      that => that.code === ErrorCode.DroppedEntry
                    )
                  : filterMode === FilterMode.FormsCompleted
                    ? !some(
                        it._issues,
                        that => that.code === ErrorCode.FormMissingResponse
                      )
                    : filterMode === FilterMode.FormsMissing
                      ? some(
                          it._issues,
                          that => that.code === ErrorCode.FormMissingResponse
                        )
                      : filterMode === FilterMode.TeamComplete
                        ? !some(
                            it._issues,
                            that => that.code === ErrorCode.TeamSizeMin
                          )
                        : filterMode === FilterMode.TeamIncomplete
                          ? some(
                              it._issues,
                              that => that.code === ErrorCode.TeamSizeMin
                            )
                          : filterMode === FilterMode.RequireNetwork
                            ? some(
                                it._issues,
                                that =>
                                  that.code === ErrorCode.MissingRequiredNetwork
                              )
                            : filterMode === FilterMode.NotRequireNetwork
                              ? !some(
                                  it._issues,
                                  that =>
                                    that.code ===
                                    ErrorCode.MissingRequiredNetwork
                                )
                              : true
      ),
    [source, filterMode]
  )

  const teams = useMemo(() => {
    return sortBy(filtered, [
      it =>
        !!nameFilterDebounced &&
        !it.alternateName?.toLocaleLowerCase().includes(nameFilterDebounced),
      it =>
        sortMode === SortMode.NameAscending
          ? (it.alternateName || '')?.toLocaleLowerCase()
          : sortMode === SortMode.NameDescending
            ? -(it.alternateName || '')?.toLocaleLowerCase()
            : sortMode === SortMode.Seed
              ? it.seed
              : 0,
      it => it.alternateName?.toLocaleLowerCase()
    ])
  }, [filtered, nameFilterDebounced, sortMode])

  const handleRefresh = useCallback(async () => {
    setLastValidated(new Date())
    mutateTeams()
  }, [mutateTeams])

  const handleAllSelected = () =>
    allChecked
      ? setSelectedTeams(
          selectedTeams.filter(it => !teams.map(it => it.id!).includes(it))
        )
      : setSelectedTeams(it => uniq([...it, ...teams?.map(it => it.id!)]))

  const handleToggleSelected = useCallback(
    (id: number) => {
      if (selectedTeams.includes(id)) {
        setSelectedTeams(selectedTeams.filter(it => it !== id))
        setLastSelected(undefined)
      } else {
        if (isShiftPressed) {
          const lastIdx = teams.findIndex(it => it.id === lastSelected)
          const thisIdx = teams.findIndex(it => it.id === id)
          const [start, end] =
            lastIdx < thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx]
          setSelectedTeams([
            ...selectedTeams,
            ...teams.slice(start, end + 1).map(it => it.id!)
          ])
          setLastSelected(id)
        } else {
          setSelectedTeams([...selectedTeams, id])
          setLastSelected(id)
        }
      }
    },
    [selectedTeams, lastSelected, isShiftPressed, teams]
  )

  const handleCheckinSelected = async () => {
    const mostRecentCheckin = last(checkinEvents.allCurrentOrPrevious)
    if (!mostRecentCheckin) {
      await handleRefresh()
      return
    }
    await checkInTeams(mostRecentCheckin?.id!, selectedTeams)
    await handleRefresh()
  }

  const handleCopyToClipboard = async () => {
    const text = teams
      .filter(it => selectedTeams.includes(it.id!))
      .map(it => it.alternateName)
      .join(', ')
    text && navigator.clipboard.writeText(text)
  }

  const handleCopyCompetitionLink = async () => {
    const url = getLinkUrl({
      root: RootRoute.Competition,
      rootId: competition?.id
    })
    url && navigator.clipboard.writeText(url)
    open(null, <RcTrans i18nKey='shared.copied-to-clipboard' />, 10000)
  }

  const options: ActionMenuOption[] = [
    {
      key: 'checkin-selected',
      display: <RcTrans i18nKey='competition:team.checkin-teams' />,
      icon: <RcIcon icon={['fal', 'check-square']} />,
      disabled: isEmpty(selectedTeams),
      confirmation: <RcTrans i18nKey='competition:team.checkin-confirm' />,
      action: handleCheckinSelected
    },
    {
      key: 'clone-teams',
      display: <RcTrans i18nKey='competition:team.clone-teams' />,
      icon: <RcIcon icon={['fal', 'clone']} />,
      disabled: isEmpty(selectedTeams),
      modal: () => <CloneTeams selected={selectedTeams} />
    },
    {
      key: 'copy-to-clipboard',
      divider: true,
      disabled: isEmpty(selectedTeams),
      display: <RcTrans i18nKey='shared.copy-to-clipboard' />,
      icon: <RcIcon icon={['fal', 'clipboard']} />,
      action: handleCopyToClipboard
    },
    {
      key: 'reindex-teams',
      disabled: isEmpty(selectedTeams),
      display: <RcTrans i18nKey='competition:team.reindex-title' />,
      icon: <RcIcon icon={['fal', 'address-book']} />,
      modal: ({ handleClose }) => (
        <TeamReindex selected={selectedTeams} onComplete={handleClose} />
      )
    }
  ]

  const someChecked = teams.some(it => selectedTeams.includes(it.id!))
  const allChecked = teams.every(it => selectedTeams.includes(it.id!))
  const filterText = filterMode ? 'matching filters' : 'total'

  const hasCheckins = some(checkinEvents.all)
  const resource = first(flat)
  const competitionForms = resource?._expanded?.competitionForm
  const hasForms = some(competitionForms)

  const memoizedRows = useMemo(
    () =>
      isEmpty(teams) ? (
        <RcTableHeaderRow>
          <RcTableCell colSpan={5}></RcTableCell>
        </RcTableHeaderRow>
      ) : (
        teams?.map((team, idx) => {
          const isPreviousSelected = selectedTeams.includes(teams[idx - 1]?.id!)
          const isNextSelected = selectedTeams.includes(teams[idx + 1]?.id!)
          return (
            <TeamsAdminTableRow
              key={team.id?.toString()}
              team={team}
              lastValidated={lastValidated}
              hasCheckins={hasCheckins}
              hasForms={hasForms}
              isSelected={selectedTeams.includes(team.id!)}
              isPreviousSelected={isPreviousSelected}
              isNextSelected={isNextSelected}
              onSelected={() => handleToggleSelected(team.id!)}
              onRefresh={handleRefresh}
              isFiltered={
                !!nameFilterDebounced &&
                !team.alternateName
                  ?.toLocaleLowerCase()
                  .includes(nameFilterDebounced.toLocaleLowerCase())
              }
            />
          )
        })
      ),
    [
      teams,
      hasCheckins,
      hasForms,
      selectedTeams,
      nameFilterDebounced,
      lastValidated,
      handleToggleSelected,
      handleRefresh
    ]
  )

  const Creation = () =>
    selfOnly ? (
      <ModalTrigger
        activation={handleOpen => (
          <Button
            className='create-team'
            onClick={handleOpen}
            variant='outlined'
            sx={{ color: 'primary.main' }}
          >
            <RcTrans i18nKey='shared.register' />
            <RcIcon icon={['fal', 'plus']} ml={2} />
          </Button>
        )}
      >
        {({ handleClose }) => (
          <JoinFlow
            onComplete={() => {
              handleClose()
              handleRefresh()
            }}
          />
        )}
      </ModalTrigger>
    ) : (
      <ModalTrigger
        activation={handleOpen => <CreateButton onClick={handleOpen} />}
      >
        {({ handleClose }) => (
          <TeamAdminCreate
            onComplete={() => {
              handleClose()
              handleRefresh()
            }}
          />
        )}
      </ModalTrigger>
    )

  return (
    <>
      <TeamAdminJoyride external={run} />
      <Box
        sx={theme => ({
          pt: 5,
          px: { xs: 3, sm: 4 },
          zIndex: theme.zIndex.appBar
        })}
      >
        <Stack direction='column'>
          <Stack
            direction='row'
            justifyContent='space-between'
            width='100%'
            alignItems='flex-end'
            pb={2}
          >
            <Typography variant='subtitle1' color='text.secondary'>
              {selectedTeams.length > 0 ? (
                <>
                  {selectedTeams.length} of {teams.length} selected
                </>
              ) : (
                <>
                  found {teams.length} {filterText}
                </>
              )}
            </Typography>

            <RcIconButton
              TooltipProps={{
                title: <RcTrans i18nKey='shared.joyride-title' />,
                PopperProps: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -10]
                      }
                    }
                  ]
                }
              }}
              onClick={() => setRun(new Date())}
              color='info'
              icon={['fal', 'question-circle']}
            />
          </Stack>
          <Stack direction='row' spacing={2}>
            <Box flexGrow={1}>
              <SearchBox filter={nameFilter} setFilter={setNameFilter} />
            </Box>
            {Creation()}
          </Stack>
        </Stack>
        <Stack
          direction='row'
          spacing={2}
          pt={2}
          pb={4}
          className='team-actions'
        >
          {selfOnly ? (
            <ConfirmingButton
              onClick={handleCheckinSelected}
              message={<RcTrans i18nKey='competition:team.checkin-confirm' />}
              disabled={
                isEmpty(selectedTeams) || isEmpty(checkinEvents.current)
              }
              variant='outlined'
              color='info'
            >
              <RcIcon
                icon={['fal', 'check-square']}
                mr={isTablet && !selfOnly ? 0 : 2}
              />
              {isTablet && !selfOnly ? null : (
                <RcTrans i18nKey='competition:team.checkin-teams' />
              )}
            </ConfirmingButton>
          ) : (
            <>
              <ModalTrigger
                modalProps={{
                  noHeader: true,
                  forceDirtyCheck: true,
                  maxWidth: 'md'
                }}
                activation={handleOpen => (
                  <Button
                    onClick={handleOpen}
                    variant='outlined'
                    color='info'
                    sx={{
                      height: '100%',
                      aspectRatio: isTablet ? 1 : undefined,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <RcIcon icon={['fal', 'seedling']} mr={isTablet ? 0 : 2} />
                    {isTablet ? null : (
                      <RcTrans i18nKey='competition:teams.actions.seed' />
                    )}
                  </Button>
                )}
              >
                {({ handleClose }) => (
                  <TeamsSeeding
                    onComplete={async teams => {
                      await updateTeams(competition?.id!, teams)
                      handleRefresh()
                      handleClose()
                    }}
                    onCancel={handleClose}
                    selected={selectedTeams}
                  />
                )}
              </ModalTrigger>

              <ModalTrigger
                activation={handleOpen => (
                  <Button
                    onClick={handleOpen}
                    disabled={isEmpty(selectedTeams)}
                    variant='outlined'
                    color='info'
                    sx={{
                      height: '100%',
                      aspectRatio: isTablet ? 1 : undefined,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <RcIcon icon={['fal', 'code-fork']} mr={isTablet ? 0 : 2} />
                    {isTablet ? null : (
                      <RcTrans i18nKey='competition:teams.actions.create-bracket' />
                    )}
                  </Button>
                )}
              >
                {({ handleClose }) => (
                  <BracketCreateEditForm
                    onComplete={handleClose}
                    onReseed={handleRefresh}
                    selectedTeams={selectedTeams}
                  />
                )}
              </ModalTrigger>

              <ModalTrigger
                activation={handleOpen => (
                  <Button
                    onClick={handleOpen}
                    disabled={isEmpty(selectedTeams)}
                    variant='outlined'
                    color='info'
                    sx={{
                      height: '100%',
                      aspectRatio: isTablet ? 1 : undefined,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <RcIcon icon={['fal', 'envelope']} mr={isTablet ? 0 : 2} />
                    {isTablet ? null : (
                      <RcTrans i18nKey='competition:teams.actions.email-teams' />
                    )}
                  </Button>
                )}
              >
                {({ handleClose }) => (
                  <ParticipantContact
                    handleClose={handleClose}
                    teams={selectedTeams}
                  />
                )}
              </ModalTrigger>
              <ActionMenu
                options={options}
                buttonProps={{
                  variant: 'outlined',
                  color: 'info',
                  sx: {}
                }}
              >
                {isTablet ? null : (
                  <RcTrans i18nKey='competition:teams.actions.more' />
                )}
                <RcIcon icon={['fal', 'chevron-down']} ml={isTablet ? 0 : 2} />
              </ActionMenu>
            </>
          )}

          <Box flexGrow={1} />

          <Button
            disabled={isValidating}
            className='spin-on-reload'
            onClick={() => handleRefresh()}
            variant='outlined'
            color='info'
            sx={isValidating ? pulse : undefined}
          >
            <RcIcon icon={['fal', 'refresh']} mr={isTablet ? 0 : 2} />
            {isTablet ? null : (
              <RcTrans i18nKey='competition:teams.actions.refresh' />
            )}
          </Button>
          <Button
            onClick={() => setFilterMode(undefined)}
            disabled={!filterMode}
            variant='outlined'
            color='info'
          >
            <RcIcon icon={['fal', 'filter-slash']} mr={isTablet ? 0 : 2} />
            {isTablet ? null : (
              <RcTrans i18nKey='competition:teams.actions.clear-filter' />
            )}
          </Button>
        </Stack>
      </Box>

      <RcSuspense>
        <RcTableContainer headerHeight={selfOnly ? 180 : 272}>
          <RcTable>
            <RcTableHead>
              <RcTableHeaderRow className='filter-teams'>
                <RcTableStickyHeaderCell>
                  <StyledCheckbox
                    sx={{ mr: 2 }}
                    className='select-teams'
                    checked={someChecked}
                    indeterminate={someChecked && !allChecked}
                    tabIndex={1}
                    onClick={(ev: any) => {
                      ev.stopPropagation()
                      ev.target.blur() // blur so copy override works
                    }}
                    onChange={handleAllSelected}
                  />
                  <Typography
                    variant='subtitle1'
                    color='text.secondary'
                    noWrap
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      cursor: 'pointer'
                    }}
                    onClick={ev =>
                      menu.open(
                        ev,
                        <TeamStatusMenu
                          filterMode={filterMode}
                          sortMode={sortMode}
                          onSelected={(filter, sort) => {
                            menu.close()
                            setFilterMode(filter)
                            setSortMode(sort)
                          }}
                        />
                      )
                    }
                  >
                    <RcTrans i18nKey='competition:teams.header.teams' />
                    <RcIcon
                      icon={['fal', 'bars-filter']}
                      color={
                        [
                          SortMode.NameAscending,
                          SortMode.NameDescending
                        ].includes(sortMode!) ||
                        [FilterMode.HasIssues, FilterMode.NoIssues].includes(
                          filterMode!
                        )
                          ? 'primary'
                          : undefined
                      }
                      ml={2}
                    />
                  </Typography>
                </RcTableStickyHeaderCell>
                {isCompetitionModerator ? (
                  <RcTableCell>{/* team note */}</RcTableCell>
                ) : null}
                {settings?.teamSizeMin && settings?.teamSizeMax ? (
                  <RcTableCell>
                    <Typography
                      variant='subtitle1'
                      color='text.secondary'
                      noWrap
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        cursor: 'pointer'
                      }}
                      onClick={ev =>
                        menu.open(
                          ev,
                          <TeamMembersMenu
                            filterMode={filterMode}
                            onSelected={mode => {
                              menu.close()
                              setFilterMode(mode)
                            }}
                          />
                        )
                      }
                    >
                      <RcTrans i18nKey='competition:teams.header.member-count' />
                      <RcIcon
                        icon={['fal', 'filter']}
                        color={
                          [
                            FilterMode.TeamComplete,
                            FilterMode.TeamIncomplete
                          ].includes(filterMode!)
                            ? 'primary'
                            : undefined
                        }
                        ml={2}
                      />
                    </Typography>
                  </RcTableCell>
                ) : null}
                {hasCheckins ? (
                  <RcTableCell>
                    <Typography
                      variant='subtitle1'
                      color='text.secondary'
                      noWrap
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        cursor: 'pointer',
                        minWidth: 80
                      }}
                      onClick={ev =>
                        menu.open(
                          ev,
                          <CheckInStatusMenu
                            filterMode={filterMode}
                            onSelected={mode => {
                              menu.close()
                              setFilterMode(mode)
                            }}
                          />
                        )
                      }
                    >
                      <RcTrans i18nKey='competition:teams.header.checkin-status' />
                      <RcIcon
                        icon={['fal', 'filter']}
                        color={
                          [
                            FilterMode.CheckedIn,
                            FilterMode.NotCheckedIn
                          ].includes(filterMode!)
                            ? 'primary'
                            : undefined
                        }
                        ml={2}
                      />
                    </Typography>
                  </RcTableCell>
                ) : null}
                {hasForms ? (
                  <RcTableCell>
                    <Typography
                      variant='subtitle1'
                      color='text.secondary'
                      noWrap
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        cursor: 'pointer',
                        minWidth: 80
                      }}
                      onClick={ev =>
                        menu.open(
                          ev,
                          <FormStatusMenu
                            filterMode={filterMode}
                            onSelected={mode => {
                              menu.close()
                              setFilterMode(mode)
                            }}
                          />
                        )
                      }
                    >
                      <RcTrans i18nKey='competition:teams.header.completed-forms' />
                      <RcIcon
                        icon={['fal', 'filter']}
                        color={
                          [
                            FilterMode.FormsCompleted,
                            FilterMode.FormsMissing
                          ].includes(filterMode!)
                            ? 'primary'
                            : undefined
                        }
                        ml={2}
                      />
                    </Typography>
                  </RcTableCell>
                ) : null}

                {settings?.requiredNetwork ? (
                  <RcTableCell>
                    <Typography
                      variant='subtitle1'
                      color='text.secondary'
                      noWrap
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        cursor: 'pointer',
                        minWidth: 80
                      }}
                      onClick={ev =>
                        menu.open(
                          ev,
                          <RequiredNetworksMenu
                            filterMode={filterMode}
                            onSelected={mode => {
                              menu.close()
                              setFilterMode(mode)
                            }}
                          />
                        )
                      }
                    >
                      <RcTrans i18nKey='competition:teams.header.require-network' />
                      <RcIcon
                        icon={['fal', 'filter']}
                        color={
                          [
                            FilterMode.RequireNetwork,
                            FilterMode.NotRequireNetwork
                          ].includes(filterMode!)
                            ? 'primary'
                            : undefined
                        }
                        ml={2}
                      />
                    </Typography>
                  </RcTableCell>
                ) : null}

                <RcTableCell>
                  <Typography
                    onClick={() => setSortMode(SortMode.Seed)}
                    variant='subtitle1'
                    color='text.secondary'
                    noWrap
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      cursor: 'pointer'
                    }}
                  >
                    <RcTrans i18nKey='competition:teams.header.seed' />
                    <RcIcon
                      icon={['fal', 'bars-filter']}
                      color={
                        [SortMode.Seed].includes(sortMode!)
                          ? 'primary'
                          : undefined
                      }
                      ml={2}
                    />
                  </Typography>
                </RcTableCell>
                {menu.PopperElement}
              </RcTableHeaderRow>
            </RcTableHead>

            {isEmpty(teams) && !isValidating ? null : (
              <RcTableBody>{memoizedRows}</RcTableBody>
            )}
          </RcTable>
        </RcTableContainer>
      </RcSuspense>
      {isEmpty(teams) && !isValidating ? (
        <PageItem contained={false}>
          <Container maxWidth='sm'>
            <Stack
              direction='column'
              spacing={3}
              alignItems='center'
              sx={{ py: 10 }}
            >
              <Typography variant='h2'>
                <RcTrans i18nKey='competition:teams.no-results-title' />
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                <RcTrans i18nKey='competition:teams.no-results-body' />
              </Typography>

              <Box py={5}>
                <RcIcon
                  icon={['fad', 'rocket-launch']}
                  size='10x'
                  color='primary'
                />
              </Box>

              <Stack direction='row' spacing={3}>
                {Creation()}
                <Button onClick={handleCopyCompetitionLink}>
                  <RcTrans i18nKey='competition:team.create-invite-link' />
                </Button>
              </Stack>
            </Stack>
          </Container>
        </PageItem>
      ) : null}
    </>
  )
}
